import { API_BASE } from './backOfficeApi';
import { BRAND } from '../lib/brand';

// Biometric step-up — the ONE place this console talks to WebAuthn.
//
// WHAT IT IS. A platform authenticator (Touch ID, Face ID, Windows Hello, Android
// biometrics) proves that the person at this keyboard is the person who enrolled this
// device, without a shared secret leaving it. The private key never goes to the server;
// the server keeps only the public key, and each enrolment is a row keyed by
// (userId, action) — so it is tied to the SIGNED-IN USER, not to the browser.
//
// WHAT IT IS NOT, and this matters: the backend's verifyAssertion does NOT check the
// signature against the stored public key, and it does not verify the challenge (its own
// comment says "we'll skip challenge verification for simplicity"). It compares the
// credential id and the clientData type. That makes this a REPLAYABLE possession check,
// not cryptographic proof. Treated here as a deliberate friction step in front of a
// sensitive action — never described in the UI as though it were security.
//
// Every string an operator sees about that lives in this file, so the claim can only be
// made in one place and can be corrected in one place.

export const BIOMETRIC_ACTIONS = {
  // The action a credential is scoped to. Matches requireBiometricPasskey({action}) on
  // POST /api/orders/boxofficeconfirm.
  confirmOrder: 'dashboardconfirm',
};

const b64urlFromBuffer = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
const b64FromBuffer = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
};
const bufferFromB64 = (s) => Uint8Array.from(atob(String(s || '').replace(/-/g, '+').replace(/_/g, '/')), (c) => c.charCodeAt(0));

const api = async (path, init = {}) => {
  const r = await fetch(`${API_BASE}/api/biometric${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(init.headers || {}) },
    ...init,
  });
  const body = await r.json().catch(() => null);
  if (!r.ok) throw new Error(body?.message || body?.error || `${r.status} ${r.statusText || 'Biometric request failed'}`);
  return body;
};

/**
 * Can this browser do it AT ALL, and can it do it with a built-in sensor?
 *
 * Two different questions, and conflating them produces the worst version of this feature:
 * an Enrol button that opens a security-key dialog on a desktop with no fingerprint reader.
 * `available` is the honest gate for showing the button.
 */
export const biometricSupport = async () => {
  if (typeof window === 'undefined' || !window.PublicKeyCredential) {
    return { supported: false, available: false, reason: 'This browser has no WebAuthn support.' };
  }
  if (!window.isSecureContext) {
    return { supported: true, available: false, reason: 'Biometrics need a secure context — HTTPS, or localhost in development.' };
  }
  try {
    const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    return {
      supported: true,
      available,
      reason: available ? null : 'This device has no built-in biometric sensor (Touch ID, Face ID, Windows Hello).',
    };
  } catch {
    return { supported: true, available: false, reason: 'Could not query this device for a biometric sensor.' };
  }
};

/** Every device the SIGNED-IN user has enrolled, newest first. */
export const listBiometricDevices = async (action) => {
  const body = await api(`/devices${action ? `?action=${encodeURIComponent(action)}` : ''}`);
  return Array.isArray(body?.devices) ? body.devices : [];
};

/** Revoke ONE device by its credential id. */
export const revokeBiometricDevice = (credentialId) =>
  api(`/devices/${encodeURIComponent(credentialId)}`, { method: 'DELETE' });

/**
 * Enrol THIS device against the signed-in user.
 *
 * Two phases against one endpoint: ask for a challenge, hand it to the authenticator, post
 * back what it produced. The first phase used to 400 because the endpoint required the
 * credential it was being asked to help create.
 */
export const enrollBiometricDevice = async ({ deviceName, action = BIOMETRIC_ACTIONS.confirmOrder }) => {
  const name = String(deviceName || '').trim();
  if (!name) throw new Error('Give this device a name you will recognise later.');

  const { supported, available, reason } = await biometricSupport();
  if (!supported || !available) throw new Error(reason || 'Biometrics are unavailable on this device.');

  const start = await api('/enroll', { method: 'POST', body: JSON.stringify({ deviceName: name, action }) });

  const credential = await navigator.credentials.create({
    publicKey: {
      challenge: bufferFromB64(start.challenge),
      rp: { name: start.rpName || `${BRAND.name} Back Office`, id: window.location.hostname },
      // The user handle is the USER's id, not the device's — that is what binds the
      // credential to the person rather than to this browser profile.
      user: {
        id: Uint8Array.from(String(start.userId || ''), (c) => c.charCodeAt(0)),
        name: start.userName || name,
        displayName: start.userName || name,
      },
      pubKeyCredParams: [{ alg: -7, type: 'public-key' }, { alg: -257, type: 'public-key' }],
      authenticatorSelection: { authenticatorAttachment: 'platform', userVerification: 'required', residentKey: 'discouraged' },
      timeout: 60000,
      attestation: 'none',
    },
  });
  if (!credential) throw new Error('Enrolment was cancelled.');

  const publicKey = credential.response.getPublicKey?.();
  if (!publicKey) throw new Error('This authenticator did not return a public key.');

  await api('/enroll', {
    method: 'POST',
    body: JSON.stringify({
      deviceName: name,
      action,
      credential: { id: b64urlFromBuffer(credential.rawId), type: credential.type, publicKey: b64FromBuffer(publicKey), counter: 0 },
    }),
  });

  return { deviceName: name, action, credentialId: b64urlFromBuffer(credential.rawId) };
};

/**
 * Prompt for the fingerprint/face and return the value for the X-Biometric-Credential
 * header. Returns null when the user has nothing enrolled for this action — the caller
 * then falls back to whatever it did before, rather than blocking on a step the operator
 * never set up.
 */
export const authorizeWithBiometric = async ({ action = BIOMETRIC_ACTIONS.confirmOrder } = {}) => {
  const devices = await listBiometricDevices(action).catch(() => []);
  if (!devices.length) return null;

  const { supported, available, reason } = await biometricSupport();
  if (!supported || !available) throw new Error(reason || 'Biometrics are unavailable on this device.');

  const assertion = await navigator.credentials.get({
    publicKey: {
      challenge: crypto.getRandomValues(new Uint8Array(32)),
      rpId: window.location.hostname,
      allowCredentials: devices.map((d) => ({ id: bufferFromB64(d.id), type: 'public-key' })),
      userVerification: 'required',
      timeout: 60000,
    },
  });
  if (!assertion) throw new Error('Authorisation was cancelled.');

  return JSON.stringify({
    id: b64urlFromBuffer(assertion.rawId),
    type: assertion.type,
    response: {
      authenticatorData: b64FromBuffer(assertion.response.authenticatorData),
      clientDataJSON: b64FromBuffer(assertion.response.clientDataJSON),
      signature: b64FromBuffer(assertion.response.signature),
      ...(assertion.response.userHandle ? { userHandle: b64FromBuffer(assertion.response.userHandle) } : {}),
    },
  });
};

/** Turn a WebAuthn DOMException into something an operator can act on. */
export const biometricErrorMessage = (e) => {
  if (!e) return 'Biometric step failed.';
  if (e.name === 'NotAllowedError') return 'Cancelled, or the prompt timed out.';
  if (e.name === 'InvalidStateError') return 'This device is already enrolled for your account.';
  if (e.name === 'SecurityError') return 'Blocked by the browser — biometrics need HTTPS or localhost.';
  return e.message || 'Biometric step failed.';
};
